import React, {FC, useEffect, useMemo, useState} from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import { useFormik } from 'formik';
import { UiDatePicker } from '@ru-digital-assets/ui-component';

import ExchangeIcon from '../../../assets/icons/exchange-icon';
import DetailsContainer from '../../../containers/details-container';
import FormFooter from '../../../containers/form-footer';
import AppPageMain from '../../../modules/app-page/app-page--main';
import IconBtn from '../../../modules/icon-btn/icon-btn';
import PageHeader from '../../../modules/page-header';
import UiInput from '../../../modules/ui/ui-input/ui-input';
import UiSelect, { OptionType } from '../../../modules/ui/ui-select/ui-select';
import { useAppDispatch } from '../../../store';
import { initExchange } from '../../../store/actions/operations-sign';
import { RootState } from '../../../types';

import './index.scss';
import {getPaymentTokens} from "../../../store/actions/common-info";

type FormStateType = {
  agentId?: number;
  tokenCountFrom?: number;
  tokenCountTo?: number;
  tokenNameFrom?: string;
  tokenNameTo?: string;
  isPublic?: boolean;
  expiryDate?: Date;
};

const initialFormState: FormStateType = {
  isPublic: false,
};

const Exchange: FC = () => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const [loadingBtn, setLoadingBtn] = useState(false);

  const availablePaymentTokens = useSelector(
    (state: RootState) => state.commonInfo.availablePaymentTokens
  );
  const paymentTypes = useSelector(
    (state: RootState) => state.commonInfo.paymentTokens
  );
  const accountId = useSelector((state: RootState) => state.auth.account.id);
  const companyInfos = useSelector(
    (state: RootState) => state.companiesInfo.companies
  );

  const availablePaymentTokensOption = useMemo(
    () =>
      availablePaymentTokens.map((item) => ({
        value: item.code,
        label: item.name + ' (доступно: ' + item.actualCount + ')',
      })),
    [availablePaymentTokens]
  );

  const paymentOptionTypes = useMemo(
    () =>
      paymentTypes.map((item) => ({
        value: item.code,
        label: item.name,
      })),
    [paymentTypes]
  );

  // TODO: mock, remove
  if (paymentOptionTypes.length === 1) {
    paymentOptionTypes.push({
      value: 'MOCK TOKEN',
      label: 'MOCK TOKEN / (выпуск 101789 от 2021-10-01)',
    });
  }
  // END TODO

  const startDate = new Date(new Date().getTime() + 1000 * 60 * 60 * 24);

  const companies: OptionType[] = useMemo(() => {
    const arr = [];
    for (let key in companyInfos) {
      if (accountId.toString() !== key) {
        arr.push({
          value: key,
          label: companyInfos[key].shortName,
        });
      }
    }
    return arr;
  }, [companyInfos]);

  const formState = useFormik({
    initialValues: initialFormState,
    onSubmit: (values) => {
      setLoadingBtn(true);
      dispatch(initExchange(values))
        .then((res) => {
          setLoadingBtn(false);
          history.push('/exchange-sign', { id: res.payload.id });
        })
        .catch(() => {
          setLoadingBtn(false);
        });
    },
  });

  useEffect(() => {
    dispatch(getPaymentTokens());
  }, []);

  return (
    <AppPageMain>
      <div className="container">
        <div className="row">
          <div className="col-19 col-lg-27 col-xl-35">
            <PageHeader
              items={[{ link: '/exchange', title: 'Обмен' }]}
              iconType="exchange"
            />
          </div>
        </div>
        <div className="buying-container">
          <DetailsContainer
            title="Обмен цифровых финансовых активов"
            onClose={() => history.back()}
            infoTitle={{
              steps: [
                { title: 'Параметры', active: true },
                { title: 'Подпись' },
                { title: 'Согласование' },
                { title: 'Обмен' },
              ],
            }}
            leftComponent={
              <form className="form-assets">
                <p className="text">Выберите токен, который хотите обменять</p>
                <div className="form-assets__item flex ">
                  <div className="assets-tabs__filter--380">
                    <UiSelect
                      placeholder="Наименование токена"
                      options={availablePaymentTokensOption.filter(
                        (p) => p.value !== formState.values.tokenNameTo
                      )}
                      lg
                      onChange={(val) => {
                        formState.setFieldValue(
                          'tokenNameFrom',
                          (val as OptionType)?.value
                        );
                      }}
                    />
                  </div>
                  <UiInput
                    required
                    rightText="шт"
                    name="tokenCountFrom"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      formState.setFieldValue(
                        'tokenCountFrom',
                        e.target.value
                          ? Number(e.target.value.replace(/\s/g, ''))
                          : null
                      );
                    }}
                    lg
                    label="Количество токенов"
                  />
                </div>
                <p className="text">На какой токен хотите обменять</p>
                <div className="form-assets__item flex">
                  <div className="assets-tabs__filter--380">
                    <UiSelect
                      placeholder="Наименование токена"
                      options={paymentOptionTypes.filter(
                        (p) => p.value !== formState.values.tokenNameFrom
                      )}
                      lg
                      onChange={(val) => {
                        formState.setFieldValue(
                          'tokenNameTo',
                          (val as OptionType)?.value
                        );
                      }}
                    />
                  </div>
                  <UiInput
                    required
                    rightText="шт"
                    name="tokenCountTo"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      formState.setFieldValue(
                        'tokenCountTo',
                        e.target.value
                          ? Number(e.target.value.replace(/\s/g, ''))
                          : null
                      );
                    }}
                    lg
                    label="Количество токенов"
                  />
                </div>
                {!formState?.values?.isPublic && (
                  <div className="form-assets__item">
                    <p className="text">
                      Укажите адресата оферты для осуществления сделки
                    </p>
                    <div className="form-item">
                      <UiSelect
                        placeholder="Адресат оферты"
                        options={companies}
                        onChange={(val) => {
                          formState.setFieldValue(
                            'agentId',
                            Number((val as OptionType)?.value)
                          );
                        }}
                      />
                    </div>
                  </div>
                )}
                <div className="form-assets__item flex">
                  <div className="form-item ">
                    <p className="text">Срок для акцепта</p>
                    <UiDatePicker
                      selected={formState.values.expiryDate}
                      minDate={startDate}
                      dateFormat="dd.MM.yyyy"
                      onChange={(date: Date) => {
                        date.setHours(date.getHours() + 3);
                        formState.setFieldValue('expiryDate', date);
                      }}
                    />
                  </div>
                  <div style={{ marginLeft: '80px', marginTop: '10px' }}>
                    <p className="text">
                      Срок, в течение которого заявка должна быть акцептована
                      Адресатом оферты, иначе сделка будет отменена.
                    </p>
                  </div>
                </div>
                <FormFooter>
                  <div className="flex justify-end">
                    <IconBtn
                      lg
                      disabled={
                        loadingBtn ||
                        !formState.values.agentId ||
                        !formState.values.expiryDate ||
                        !formState.values.tokenCountFrom ||
                        !formState.values.tokenCountTo ||
                        !formState.values.tokenNameFrom ||
                        !formState.values.tokenNameTo ||
                        !formState.isValid
                      }
                      onClick={formState.handleSubmit}
                      icon={<ExchangeIcon color="white" />}
                      title="Сформировать заявку на обмен ЦФА"
                    />
                  </div>
                </FormFooter>
              </form>
            }
          />
        </div>
      </div>
    </AppPageMain>
  );
};

export default Exchange;
